import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Play, Clock, Loader2, Eye } from 'lucide-react';

const AUTOPLAY_COUNTDOWN_SEC = 5;
const WATCH_HISTORY_KEY = 'afrosports_watched_ids';

// localStorage helpers for anonymous users
function getLocalWatched() {
  try { return JSON.parse(localStorage.getItem(WATCH_HISTORY_KEY) || '[]'); } catch { return []; }
}
function addLocalWatched(id) {
  const ids = getLocalWatched();
  if (!ids.includes(id)) { ids.unshift(id); localStorage.setItem(WATCH_HISTORY_KEY, JSON.stringify(ids.slice(0, 200))); }
}

export function VideoPlayerModal({ video, onClose }) {
  const [watchNext, setWatchNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [countdown, setCountdown] = useState(AUTOPLAY_COUNTDOWN_SEC);
  const [autoplayCancelled, setAutoplayCancelled] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(video);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const countdownRef = useRef(null);
  const playerRef = useRef(null);

  // ESC to close
  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  // Load watch history (API + localStorage merge)
  useEffect(() => {
    const localIds = getLocalWatched();
    const merged = new Set(localIds);

    // Try API (logged-in users)
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      fetch('/api/content/watch-history', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.watched) data.watched.forEach(id => merged.add(id));
          setWatchedIds(new Set(merged));
        })
        .catch(() => setWatchedIds(new Set(merged)));
    } else {
      setWatchedIds(new Set(merged));
    }
  }, []);

  // Record a watch
  const recordWatch = useCallback((videoId) => {
    addLocalWatched(videoId);
    setWatchedIds(prev => new Set([...prev, videoId]));

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      fetch('/api/content/watch-history', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: videoId }),
      }).catch(() => {});
    }
  }, []);

  // Record current video on play
  useEffect(() => {
    if (currentVideo?.id) recordWatch(currentVideo.id);
  }, [currentVideo?.id, recordWatch]);

  // Fetch watch-next with watched filter
  useEffect(() => {
    if (!currentVideo?.id) return;
    setLoading(true);
    setVideoEnded(false);
    setAutoplayCancelled(false);
    setCountdown(AUTOPLAY_COUNTDOWN_SEC);
    if (countdownRef.current) clearInterval(countdownRef.current);

    const watchedParam = [...watchedIds].join(',');
    fetch(`/api/content/highlights/watch-next?video_id=${currentVideo.id}&watched=${watchedParam}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setWatchNext(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentVideo?.id, watchedIds]);

  // YouTube IFrame API — detect video end
  useEffect(() => {
    if (!currentVideo?.youtube_video_id) return;

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }

    const initPlayer = () => {
      if (playerRef.current) { try { playerRef.current.destroy(); } catch (_) {} }
      playerRef.current = new window.YT.Player('yt-embed-player', {
        videoId: currentVideo.youtube_video_id,
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) setVideoEnded(true);
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      setTimeout(initPlayer, 100);
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => { if (playerRef.current) { try { playerRef.current.destroy(); } catch (_) {} playerRef.current = null; } };
  }, [currentVideo?.youtube_video_id]);

  // Autoplay countdown
  useEffect(() => {
    if (!videoEnded || autoplayCancelled || !watchNext?.autoplay_next) return;
    setCountdown(AUTOPLAY_COUNTDOWN_SEC);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); setCurrentVideo(watchNext.autoplay_next); return AUTOPLAY_COUNTDOWN_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [videoEnded, autoplayCancelled, watchNext?.autoplay_next]);

  const playVideo = (v) => {
    setVideoEnded(false);
    setAutoplayCancelled(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCurrentVideo(v);
  };

  const cancelAutoplay = () => {
    setAutoplayCancelled(true);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  if (!currentVideo) return null;

  const hasRecs = watchNext && (watchNext.same_match?.length > 0 || watchNext.same_competition?.length > 0 || watchNext.same_team?.length > 0);

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" data-testid="video-player-modal">
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} data-testid="video-modal-backdrop" />

      <div className="relative z-10 flex flex-col items-center min-h-full py-8 px-4 sm:px-6">
        <div className="w-full max-w-5xl flex justify-end mb-3">
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="video-modal-close" aria-label="Close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="w-full max-w-5xl">
          {/* Player */}
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-2xl shadow-black/60">
            {currentVideo.youtube_video_id ? (
              <div id="yt-embed-player" className="absolute inset-0 w-full h-full" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <p className="text-white/40 text-sm">Video unavailable</p>
              </div>
            )}

            {/* Autoplay countdown overlay */}
            {videoEnded && watchNext?.autoplay_next && !autoplayCancelled && (
              <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-20" data-testid="autoplay-countdown">
                <p className="text-white/60 text-sm mb-3">Up next in</p>
                <div className="relative w-20 h-20 mb-4">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                    <circle cx="40" cy="40" r="36" fill="none" stroke="#10b981" strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 36}`}
                      strokeDashoffset={`${2 * Math.PI * 36 * (1 - countdown / AUTOPLAY_COUNTDOWN_SEC)}`}
                      strokeLinecap="round" className="transition-all duration-1000 ease-linear" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold">{countdown}</span>
                </div>
                <p className="text-white font-semibold text-sm mb-1 text-center max-w-xs line-clamp-2">{watchNext.autoplay_next.title}</p>
                <div className="flex items-center gap-3 mt-4">
                  <button onClick={() => playVideo(watchNext.autoplay_next)} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-full transition-colors" data-testid="autoplay-play-now">
                    <Play className="w-4 h-4 inline mr-1" fill="currentColor" /> Play Now
                  </button>
                  <button onClick={cancelAutoplay} className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-full transition-colors" data-testid="autoplay-cancel">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Video info */}
          <div className="mt-4 px-1">
            <h3 className="text-white font-bold text-lg line-clamp-2" data-testid="video-modal-title">{currentVideo.title}</h3>
            <div className="flex items-center gap-3 mt-1.5 text-white/40 text-xs">
              {(currentVideo.competition_name || currentVideo.competition) && (
                <span className="text-emerald-400 font-medium">{currentVideo.competition_name || currentVideo.competition}</span>
              )}
              {currentVideo.video_type && <span className="px-2 py-0.5 bg-white/5 rounded capitalize">{currentVideo.video_type}</span>}
              {currentVideo.published_at && (
                <span>{new Date(currentVideo.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              )}
            </div>
          </div>

          {/* Watch Next */}
          {loading && <div className="mt-8 flex items-center gap-2 text-white/30 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading recommendations...</div>}

          {!loading && hasRecs && (
            <div className="mt-8 space-y-6" data-testid="watch-next-section">
              {watchNext.same_match?.length > 0 && (
                <WatchNextGroup title="Same Match" subtitle="Extended Highlights & Goals" videos={watchNext.same_match} onPlay={playVideo} />
              )}
              {watchNext.same_competition?.length > 0 && (
                <WatchNextGroup title="Same Competition" subtitle="Related Matches" videos={watchNext.same_competition} onPlay={playVideo} />
              )}
              {watchNext.same_team?.length > 0 && (
                <WatchNextGroup title="Same Team" subtitle="More highlights from this team" videos={watchNext.same_team} onPlay={playVideo} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WatchNextGroup({ title, subtitle, videos, onPlay }) {
  return (
    <div data-testid={`watch-next-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-white font-bold text-sm">{title}</h4>
        <span className="text-white/25 text-xs">{subtitle}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {videos.map(v => (
          <button
            key={v.id}
            onClick={() => onPlay(v)}
            className="group text-left rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/30 hover:bg-white/[0.05] transition-all"
            data-testid={`watch-next-video-${v.id}`}
          >
            <div className="relative aspect-video bg-black/40">
              {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                <Play className="w-8 h-8 text-white" fill="currentColor" />
              </div>
              {v.duration > 0 && (
                <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded">
                  {Math.floor(v.duration / 60)}:{String(v.duration % 60).padStart(2, '0')}
                </span>
              )}
              {/* Watched badge */}
              {v.is_watched && (
                <span className="absolute top-1 left-1 flex items-center gap-1 bg-black/70 text-white/50 text-[9px] px-1.5 py-0.5 rounded" data-testid="watched-badge">
                  <Eye className="w-3 h-3" /> Watched
                </span>
              )}
            </div>
            <div className="p-2">
              <p className={`text-xs font-medium line-clamp-2 leading-tight group-hover:text-white transition-colors ${v.is_watched ? 'text-white/40' : 'text-white/80'}`}>
                {v.title}
              </p>
              {v.competition && <span className="text-white/25 text-[10px] mt-1 block">{v.competition}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
